import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SignatureAppearance.module.css';

/**
 * Orden visual final:
 *   1) Logo (si showLogo && logoSrc)
 *   2) Firma/Imagen custom (si signatureSrc)
 *   3) Textos (name/date/freeText)
 */
const SignatureAppearance = memo(function SignatureAppearance({
                                                                  logoSrc,                  // string | null  -> logotipo institucional
                                                                  signatureSrc,             // string | null  -> firma manuscrita / imagen del usuario
                                                                  name,
                                                                  freeText,
                                                                  date,
                                                                  showName = true,
                                                                  showDate = false,
                                                                  showFreeText = false,
                                                                  showLogo = true,          // controla visibilidad del logo
                                                                  alignment = 'center',     // 'left' | 'center' | 'right'
                                                                  className = '',
                                                                  logoMaxHeight = 65 ,
                                                                  nameItalic = false,
                                                                  selectedFont = 'inherit',
                                                                  showLabels =false,
                                                                  logoPosition = 'BEFORE_TEXT', // BEFORE_TEXT | AFTER_TEXT | RIGHT_OF_TEXT | LEFT_OF_TEXT | WATERMARK

                                                              }) {
    const { t } = useTranslation();
    const noText = !showName && !showDate && !(showFreeText && freeText);

    const alignClass = useMemo(() => {
        switch (alignment) {
            case 'left': return styles.alignLeft;
            case 'right': return styles.alignRight;
            default: return styles.alignCenter;
        }
    }, [alignment]);

    const hasLogo = !!(showLogo && logoSrc);
    const hasSignature = !!signatureSrc;

    const layoutClass = useMemo(() => {
        switch ((logoPosition || '').toUpperCase()) {
            case 'RIGHT_OF_TEXT': return styles.logoRight;
            case 'LEFT_OF_TEXT': return styles.logoLeft;
            case 'WATERMARK': return styles.logoWatermark;
            default: return styles.logoColumn; // BEFORE_TEXT / AFTER_TEXT
        }
    }, [logoPosition]);


    const rootMods = [
        noText ? styles.noText : '',
        hasLogo ? styles.hasLogo : '',
        hasSignature ? styles.hasSignature : '',
    ].join(' ');

    const Logo = hasLogo ? (
        <img className={styles.logo} src={logoSrc} alt="" draggable={false} />
    ) : null;

    const SignatureImg = hasSignature ? (
        <img className={styles.signatureImg} src={signatureSrc} alt="" draggable={false} />
    ) : null;

    const Texts = (
        <div className={styles.textBlock}>
            {showName && !!name && (
                <div
                    className={[styles.line, styles.nameLine, nameItalic ? styles.nameCursive : ''].join(' ')}
                >
                    {name}
                </div>
            )}

            {showFreeText && !!freeText && (
                <div
                    className={[styles.line, styles.freeTextLine].join(' ')}
                    style={{ fontFamily: selectedFont === 'inherit' ? 'inherit' : selectedFont }}
                    dangerouslySetInnerHTML={{ __html: freeText }}
                />
            )}

            {showDate && !!date && (
                <div className={[styles.line, styles.dateLine].join(' ')}>
                    {showLabels && <span>{t('signatureAppearance.date-label')}: </span>}
                    <span data-appearance-date>{date}</span>
                </div>
            )}
        </div>
    );

    const normalizedPos = (logoPosition || '').toUpperCase();
    return (
        <div
            className={`${styles.signatureField} ${alignClass} ${layoutClass} ${rootMods} ${className}`}
            style={{ '--logo-max-h': `${logoMaxHeight}px` }}
        >
            {/* WATERMARK: el logo va “detrás” (no afecta el flujo) */}
            {normalizedPos === 'WATERMARK' && Logo}

            {/* LEFT/RIGHT: layout en fila */}
            {normalizedPos === 'LEFT_OF_TEXT' && Logo}

            <div className={styles.content}>
                {/* BEFORE_TEXT: logo antes del bloque (texto + firma manuscrita/imagen) */}
                {normalizedPos === 'BEFORE_TEXT' && Logo}

                {SignatureImg}
                {Texts}

                {/* AFTER_TEXT: logo al final de los atributos de texto (lo ponemos al final del bloque) */}
                {normalizedPos === 'AFTER_TEXT' && Logo}
            </div>

            {normalizedPos === 'RIGHT_OF_TEXT' && Logo}
        </div>
    );
});

export default SignatureAppearance;
